import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CenterScreenModal } from '../atoms'
import { Col, Row } from 'react-grid-system';
import { useShowGalleryModalContext } from '@/customHooks/useShowGalleryModalContext';

const GalleryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 90vh; /* Ocupa casi toda la altura de la ventana */
  width: 90vw; /* Ocupa casi todo el ancho de la ventana */
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  overflow: hidden;
  padding: 1rem;
  box-sizing: border-box;
`;

const MainImageContainer = styled.div`
  flex: 1;
  min-height: 50vh;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  margin-bottom: 1rem;
  background: #000;
`;

const MainImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const MainVideo = styled.video`
  width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const ThumbnailsContainer = styled(Row)`
  flex-shrink: 0;
  overflow-x: auto;
  padding-bottom: 0.5rem; /* Espacio para la barra de scroll */
  
  /* Estilos para la barra de scroll */
  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const ThumbnailImage = styled.img<{ $isActive: boolean }>`
  width: 100px;
  height: 75px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  border: 3px solid ${({ $isActive, theme }) => ($isActive ? theme.colors.primary1 : 'transparent')};
  transition: border-color 0.2s ease-in-out;
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.7)};

  &:hover {
    opacity: 1;
  }
`;

const ThumbnailVideo = styled.video<{ $isActive: boolean }>`
  width: 100px;
  height: 75px;
  display: block;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  border: 3px solid ${({ $isActive, theme }) => ($isActive ? theme.colors.primary1 : 'transparent')};
  transition: border-color 0.2s ease-in-out;
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.7)};
  background: #000;

  &:hover {
    opacity: 1;
  }
`;

export const ShowGalleryPictureModal = () => {
  const { pictures, isOpen, closeModal } = useShowGalleryModalContext();
  
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const urlsToRevokeRef = useRef<string[]>([]);

  useEffect(() => {
    setSelectedIndex(0);

    if (isOpen && pictures.length > 0) {
      urlsToRevokeRef.current.forEach((url) => URL.revokeObjectURL(url));
      urlsToRevokeRef.current = [];

      const newImageUrls = pictures.map((p) => URL.createObjectURL(p));
      urlsToRevokeRef.current = newImageUrls;
      setImageUrls(newImageUrls);

      return () => {
      };
    }

    setImageUrls([]);
    return () => {};
  }, [pictures, isOpen]);

  useEffect(() => {
    return () => {
      urlsToRevokeRef.current.forEach((url) => URL.revokeObjectURL(url));
      urlsToRevokeRef.current = [];
    };
  }, []);

  const isVideoFile = (file: File) => file.type?.startsWith('video/');

  return (
    <CenterScreenModal isOpen={isOpen} onClose={closeModal}>
      <GalleryWrapper>
        <MainImageContainer>
          {imageUrls.length > 0 && pictures[selectedIndex] && (
            isVideoFile(pictures[selectedIndex]) ? (
              <MainVideo
                src={imageUrls[selectedIndex]}
                controls
                preload="auto"
                playsInline
                key={imageUrls[selectedIndex]}
              />
            ) : (
              <MainImage src={imageUrls[selectedIndex]} alt={`Picture ${selectedIndex + 1}`} />
            )
          )}
        </MainImageContainer>
        <ThumbnailsContainer nogutter>
          {imageUrls.map((url, index) => {
            const isVideo = isVideoFile(pictures[index]);
            return (
              <Col key={`${url}-${index}`} xs="content" style={{ padding: '0 4px' }}>
                {isVideo ? (
                  <ThumbnailVideo
                    src={url}
                    muted
                    preload="auto"
                    playsInline
                    $isActive={index === selectedIndex}
                    onClick={() => setSelectedIndex(index)}
                  />
                ) : (
                  <ThumbnailImage src={url} alt={`Thumbnail ${index + 1}`} $isActive={index === selectedIndex} onClick={() => setSelectedIndex(index)} />
                )}
              </Col>
            );
          })}
        </ThumbnailsContainer>
      </GalleryWrapper>
    </CenterScreenModal>
  );
};
